// For the shared `.chart-renderer-tooltip` surface the sparkline tooltip below reuses — same reason
// TableRenderer imports it. Not inherited from ChartRenderer.tsx: a direct import of this component
// (a story, a future barrel export) would otherwise render an unstyled tooltip.
import './ChartRenderer.scss';
import './StatRenderer.scss';
import cn from 'classnames';
import { ReactNode, useId } from 'react';
import { MdCallMade } from 'react-icons/md';
import { Area, AreaChart, Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  CellValue,
  ChartConfig,
  QueryResponse,
  FieldMetaCtx
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Brand0, Brand30, Brand50 } from '../../utils/colors';
import {
  formatChartValue,
  formatComparisonDate,
  PreparedStat,
  prepareStat
} from './chart-renderer-utils';

interface StatRendererProps {
  config: ChartConfig;
  data: QueryResponse;
  className?: string;
  showTitle?: boolean;
  ctx?: FieldMetaCtx;
  // Pickers drawn on the value's row, so the value sits at the same height with or without them.
  controls?: ReactNode;
}

// Renders a `stat` ChartConfig: the big aggregate value, an optional up/down-colored compare badge,
// and an optional trend chart. Falls back to a placeholder when the value can't be resolved
// (mirrors ChartRenderer's graphic/table fallbacks).
//
// `encoding.label` is deliberately NOT drawn under the value: it names the same metric the card
// title already names ("Total Spend" over "Total Spend"), so on a card it read as a stutter. It is
// still used -- as the series name in the sparkline's hover tooltip, where nothing else identifies
// the metric.
//
// Laid out as sub-header / body / footer rather than one flat stack, so the trend can take the
// card's whole remaining height and bleed to its edges the way the legacy SparkChart's does. The
// horizontal padding therefore lives on the text blocks, not on the root.
export const StatRenderer = ({
  config,
  data,
  className,
  showTitle = true,
  ctx,
  controls
}: StatRendererProps) => {
  const prepared = prepareStat(config, data, ctx);
  if (!prepared) {
    return (
      <div className={cn('stat-renderer', 'stat-renderer-placeholder', className)}>
        <span className='refetch-dimmable'>
          Couldn&apos;t map this stat&apos;s data to its encoding.
        </span>
        {controls && <div className='stat-renderer-controls'>{controls}</div>}
      </div>
    );
  }

  // `sparklineVariant` is a display hint on the encoding; the filled area is the house style, so an
  // unset hint gets the area rather than the bare line.
  const variant = config.type === 'stat' ? config.encoding.sparklineVariant : undefined;

  return (
    <div className={cn('stat-renderer', className)}>
      <div className='stat-renderer-header'>
        {showTitle && config.title && (
          <h3 className='stat-renderer-title refetch-dimmable'>{config.title}</h3>
        )}
        <div className='stat-renderer-value-row'>
          <div className='stat-renderer-value refetch-dimmable'>
            {prepared.formatValue(prepared.value)}
          </div>
          {controls && <div className='stat-renderer-controls'>{controls}</div>}
        </div>
      </div>
      {prepared.sparkline && (
        <StatSparkline
          sparkline={prepared.sparkline}
          formatValue={prepared.formatValue}
          label={prepared.label}
          variant={variant ?? 'area'}
        />
      )}
      {prepared.compare && (
        <div className='stat-renderer-footer refetch-dimmable'>
          <StatCompareBadge compare={prepared.compare} />
        </div>
      )}
    </div>
  );
};

interface StatCompareBadgeProps {
  compare: NonNullable<PreparedStat['compare']>;
}

// The compare delta as a pill: arrow + formatted value. Filled green when the change is "good" for
// the metric, grey when it isn't or when the direction is undefined. Keying the fill off `isGood`
// rather than the raw sign is what makes an inverted metric (CPC, CPM) color correctly without the
// renderer keeping a list of which metrics those are.
const StatCompareBadge = ({ compare }: StatCompareBadgeProps) => {
  if (compare.delta === null) return undefined;

  // ratio shows a bare multiple (e.g. "1.2x"); deltas show the formatted change.
  const text =
    compare.mode === 'ratio'
      ? `${formatChartValue(compare.delta, compare.format)}x`
      : formatChartValue(compare.delta, compare.format);

  return (
    <div className={cn('stat-renderer-compare', { good: compare.isGood === true })}>
      {compare.mode !== 'ratio' && (
        <MdCallMade
          className={cn('stat-renderer-compare-arrow', { down: compare.delta < 0 })}
          aria-hidden
        />
      )}
      <span className='stat-renderer-compare-value'>{text}</span>
    </div>
  );
};

interface StatSparklineProps {
  sparkline: NonNullable<PreparedStat['sparkline']>;
  formatValue: (value: CellValue) => string;
  label?: string;
  variant: 'line' | 'area';
}

// The trend beneath the value. No axes, grid, or legend — it reads as a glyph, and the hover
// tooltip carries the numbers instead.
const StatSparkline = ({ sparkline, formatValue, label, variant }: StatSparklineProps) => {
  // A document-wide SVG id, so two stat cards on one dashboard would otherwise share one gradient
  // (and the second card's fill would silently resolve to the first's). useId's own delimiters
  // aren't valid in a plain id, hence the strip.
  const gradientId = `stat-sparkline-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const Chart = variant === 'area' ? AreaChart : LineChart;

  return (
    <div className='stat-renderer-sparkline refetch-dimmable'>
      <ResponsiveContainer width='100%' height='100%'>
        <Chart data={sparkline.points} margin={SPARKLINE_MARGIN}>
          <Tooltip
            cursor={false}
            content={<StatSparklineTooltip formatValue={formatValue} seriesLabel={label} />}
          />
          {variant === 'area' ? (
            <>
              <defs>
                <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='1%' stopColor={Brand30} />
                  <stop offset='95%' stopColor={Brand0} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                dataKey='y'
                type='linear'
                stroke={Brand50}
                strokeWidth={SPARKLINE_STROKE_WIDTH}
                fill={`url(#${gradientId})`}
                fillOpacity={1}
                activeDot={false}
                isAnimationActive={false}
              />
            </>
          ) : (
            <Line
              dataKey='y'
              type='linear'
              stroke={Brand50}
              strokeWidth={SPARKLINE_STROKE_WIDTH}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
};

// Zero on every side: the trend is meant to bleed to the card's edges, and recharts' default
// margin would inset it into a box.
const SPARKLINE_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

const SPARKLINE_STROKE_WIDTH = 3;

interface StatSparklineTooltipProps {
  formatValue: (value: CellValue) => string;
  // NOT `label`: recharts clones the `content` element with its own props, and one of them is
  // `label` (the hovered category). A prop of that name here is silently overwritten by it.
  seriesLabel?: string;
  // Supplied by recharts, which is why both are optional.
  active?: boolean;
  payload?: { payload?: { x?: CellValue; y?: CellValue } }[];
}

// Reuses ChartRenderer's tooltip surface classes rather than minting a second one, so the stat's
// tooltip and the graphic charts' stay identical as that surface changes.
const StatSparklineTooltip = ({
  formatValue,
  seriesLabel,
  active,
  payload
}: StatSparklineTooltipProps) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  // x is the bucket from the sparkline query's dimension column (typically an ISO date), or a bare
  // row index when that query carried no dimension. An index names nothing, so it gets no header.
  const header = formatComparisonDate(point.x ?? null);

  return (
    <div className='chart-renderer-tooltip'>
      {header && <div className='chart-renderer-tooltip-label'>{header}</div>}
      <div className='chart-renderer-tooltip-row'>
        {seriesLabel && <span className='chart-renderer-tooltip-name'>{seriesLabel}</span>}
        <span className='chart-renderer-tooltip-value'>{formatValue(point.y ?? null)}</span>
      </div>
    </div>
  );
};
